// EditRequests.js — admin page for PPC edit request approval
// Redesigned to match cold-store approval-requests visual language:
//   tabs (ALL/PENDING/APPROVED/REJECTED with counts), table layout, colored
//   pills, dashboard chip, expandable row showing old/new values, modal for
//   reject reason. Backend: edit_requests.php (action=list/approve/reject).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePPCAuth, PPC_API_URL } from './PPCContext';

const DASHBOARDS = [
  { key: 'all',   label: 'All dashboards' },
  { key: 'bravo', label: 'BRAVO' },
  { key: 'papa',  label: 'PAPA' },
  { key: 'golf',  label: 'GOLF' },
  { key: 'romeo', label: 'ROMEO' },
];

// Pill colors per dashboard (matches mockup)
const dashPill = (d) => {
  switch ((d || '').toLowerCase()) {
    case 'bravo': return { bg: '#fee2e2', fg: '#991b1b' };
    case 'papa':  return { bg: '#fef3c7', fg: '#92400e' };
    case 'golf':  return { bg: '#dcfce7', fg: '#166534' };
    case 'romeo': return { bg: '#ede9fe', fg: '#5b21b6' };
    default:      return { bg: '#e5e7eb', fg: '#374151' };
  }
};

const fmtDateTime = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EditRequests = () => {
  const { user } = usePPCAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [dashboardFilter, setDashboardFilter] = useState('all');
  const [contractorSearch, setContractorSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  // Server-side filtering for date + dashboard; client-side for tab + contractor search
  const fetchAllRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: 'all', limit: '500' });
      if (dashboardFilter !== 'all') params.set('dashboard', dashboardFilter);
      if (dateFrom && dateTo) {
        params.set('from', dateFrom);
        params.set('to', dateTo);
      }
      const url = `${PPC_API_URL}/edit_requests.php?action=list&${params.toString()}`;
      const res = await axios.get(url, getAuthHeader());
      if (res.data && res.data.success) {
        setAllRequests(res.data.requests || []);
      } else {
        showToast('Failed to fetch requests', 'error');
      }
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      showToast(err.response?.data?.error || 'Failed to fetch requests', 'error');
    }
    setLoading(false);
  }, [dashboardFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/ppc');
      return;
    }
    fetchAllRequests();
  }, [isAdmin, navigate, fetchAllRequests]);

  // Client-side counts for tab badges
  const counts = useMemo(() => ({
    all:      allRequests.length,
    pending:  allRequests.filter((r) => r.status === 'pending').length,
    approved: allRequests.filter((r) => r.status === 'approved').length,
    rejected: allRequests.filter((r) => r.status === 'rejected').length,
  }), [allRequests]);

  // Client-side filtered list shown in the table
  const visibleRequests = useMemo(() => {
    let list = allRequests;
    if (activeTab !== 'all') list = list.filter((r) => r.status === activeTab);
    const q = contractorSearch.trim().toLowerCase();
    if (q !== '') {
      list = list.filter((r) => (r.contractor || '').toLowerCase().includes(q));
    }
    return list;
  }, [allRequests, activeTab, contractorSearch]);

  const handleApprove = async (requestId) => {
    try {
      const res = await axios.put(
        `${PPC_API_URL}/edit_requests.php?action=approve`,
        { request_id: requestId },
        getAuthHeader()
      );
      if (res.data && res.data.success) {
        showToast('Request approved and changes applied');
        fetchAllRequests();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to approve request', 'error');
    }
  };

  const openRejectModal = (requestId) => {
    setRejectModal(requestId);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (rejectModal == null) return;
    try {
      const res = await axios.put(
        `${PPC_API_URL}/edit_requests.php?action=reject`,
        { request_id: rejectModal, review_notes: rejectReason },
        getAuthHeader()
      );
      if (res.data && res.data.success) {
        showToast('Request rejected');
        setRejectModal(null);
        setRejectReason('');
        fetchAllRequests();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to reject request', 'error');
    }
  };

  const resetFilters = () => {
    setDashboardFilter('all');
    setContractorSearch('');
    setDateFrom('');
    setDateTo('');
  };

  const tabs = [
    { key: 'all',      label: 'ALL REQUESTS' },
    { key: 'pending',  label: 'PENDING' },
    { key: 'approved', label: 'APPROVED' },
    { key: 'rejected', label: 'REJECTED' },
  ];

  return (
    <div className="edit-requests-page">
      {toast.show && (
        <div className={`toast ${toast.type}`}>{toast.message}</div>
      )}

      {rejectModal !== null && (
        <div className="modal-backdrop" onClick={() => setRejectModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reject Request #{rejectModal}</h3>
            <p>Provide an optional reason. The editor will see it as a notification.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Insufficient justification, please re-submit with proof"
              rows={4}
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button>
              <button className="btn-danger" onClick={submitReject}>Reject Request</button>
            </div>
          </div>
        </div>
      )}

      <header className="page-header">
        <button className="back-btn" onClick={() => navigate('/ppc')}>← Back</button>
        <h1>Edit Requests Management</h1>
      </header>

      <div className="content-wrap">
        <div className="card">
          {/* Title row with icon + REFRESH */}
          <div className="card-title">
            <div className="title-left">
              <div className="title-icon">✓</div>
              <span className="title-text">EDIT REQUESTS</span>
            </div>
            <button className="refresh-btn" onClick={fetchAllRequests}>↻ REFRESH</button>
          </div>

          {/* Tabs */}
          <div className="tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                className={`tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label} ({counts[t.key]})
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="filters">
            <select value={dashboardFilter} onChange={(e) => setDashboardFilter(e.target.value)}>
              {DASHBOARDS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <input
              type="text"
              placeholder="Search contractor..."
              value={contractorSearch}
              onChange={(e) => setContractorSearch(e.target.value)}
            />
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <input type="date" value={dateTo}   onChange={(e) => setDateTo(e.target.value)} />
            {(dashboardFilter !== 'all' || contractorSearch || dateFrom || dateTo) && (
              <button className="reset-btn" onClick={resetFilters}>Reset</button>
            )}
          </div>

          {/* Table */}
          <div className="table-wrap">
            {loading ? (
              <div className="empty-state">Loading requests...</div>
            ) : visibleRequests.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <div className="empty-title">No {activeTab === 'all' ? '' : activeTab} requests</div>
                <div className="empty-sub">
                  There are no {activeTab === 'all' ? '' : activeTab} edit requests matching the current filters.
                </div>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>DASHBOARD</th>
                    <th>REQUESTED BY</th>
                    <th>ENTRY</th>
                    <th>CONTRACTOR</th>
                    <th>REASON</th>
                    <th>STATUS</th>
                    <th>CREATED</th>
                    <th style={{ textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRequests.map((req) => {
                    const dp = dashPill(req.dashboard);
                    const isPending = req.status === 'pending';
                    const isExpanded = expandedRow === req.id;
                    return (
                      <React.Fragment key={req.id}>
                        <tr
                          className={`${isPending ? 'row-pending' : ''} ${isExpanded ? 'row-expanded' : ''}`}
                          onClick={() => setExpandedRow(isExpanded ? null : req.id)}
                        >
                          <td className="cell-id">#{req.id}</td>
                          <td>
                            <span
                              className="pill"
                              style={{ background: dp.bg, color: dp.fg }}
                            >
                              {(req.dashboard || '').toUpperCase()}
                            </span>
                          </td>
                          <td className="cell-muted">{req.requested_by_name || req.requested_by_username || '-'}</td>
                          <td className="cell-muted">
                            #{req.entry_id}{req.shift ? ` · ${req.shift}` : ''}
                          </td>
                          <td className="cell-strong">{req.contractor || '-'}</td>
                          <td className="cell-reason" title={req.reason || ''}>{req.reason || '-'}</td>
                          <td>
                            <span className={`status-pill status-${req.status}`}>
                              {(req.status || '').toUpperCase()}
                            </span>
                          </td>
                          <td className="cell-time">{fmtDateTime(req.created_at)}</td>
                          <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
                            {isPending ? (
                              <>
                                <button className="btn-approve" onClick={() => handleApprove(req.id)}>
                                  ✓ APPROVE
                                </button>
                                <button className="btn-reject" onClick={() => openRejectModal(req.id)}>
                                  ✗ REJECT
                                </button>
                              </>
                            ) : (
                              <span className="cell-audit">
                                {req.status === 'approved' ? 'Approved' : 'Rejected'}
                                {req.reviewed_by_name ? ` by ${req.reviewed_by_name}` : ''}
                              </span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="row-detail">
                            <td colSpan={9}>
                              <div className="detail-grid">
                                <div>
                                  <h4>Current Values</h4>
                                  {req.old_values && Object.keys(req.old_values).length > 0 ? (
                                    <ul>
                                      {Object.entries(req.old_values).map(([k, v]) => (
                                        <li key={k}><span className="kv-key">{k}:</span> {String(v)}</li>
                                      ))}
                                    </ul>
                                  ) : <p className="muted">No before snapshot</p>}
                                </div>
                                <div>
                                  <h4>Requested Changes</h4>
                                  {req.new_values && Object.keys(req.new_values).length > 0 ? (
                                    <ul>
                                      {Object.entries(req.new_values).map(([k, v]) => (
                                        <li key={k}>
                                          <span className="kv-key">{k}:</span> <strong>{String(v)}</strong>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : <p className="muted">No changes recorded</p>}
                                </div>
                                <div>
                                  <h4>Meta</h4>
                                  <ul>
                                    <li><span className="kv-key">Entry date:</span> {req.entry_date || '-'}</li>
                                    <li><span className="kv-key">Submitted:</span> {fmtDateTime(req.created_at)}</li>
                                    {req.reviewed_at && (
                                      <li><span className="kv-key">Reviewed:</span> {fmtDateTime(req.reviewed_at)}</li>
                                    )}
                                    {req.review_notes && (
                                      <li><span className="kv-key">Review notes:</span> {req.review_notes}</li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .edit-requests-page {
          min-height: 100vh;
          background: #f0f2f5;
        }

        .toast {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 12px 24px;
          border-radius: 8px;
          color: white;
          font-weight: 500;
          z-index: 9999;
          animation: slideIn 0.3s ease-out;
        }
        .toast.success { background: linear-gradient(135deg, #2e7d32, #388e3c); }
        .toast.error   { background: linear-gradient(135deg, #c62828, #e53935); }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0); opacity: 1; }
        }

        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          z-index: 9998;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
        }
        .modal {
          background: white;
          border-radius: 12px;
          padding: 22px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 12px 40px rgba(0,0,0,0.3);
        }
        .modal h3 { margin: 0 0 8px; font-size: 18px; color: #111827; }
        .modal p { margin: 0 0 14px; color: #6b7280; font-size: 13px; }
        .modal textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-family: inherit;
          font-size: 13px;
          resize: vertical;
          box-sizing: border-box;
        }
        .modal-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 16px;
        }
        .btn-secondary {
          background: white;
          border: 1px solid #d1d5db;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          color: #374151;
        }
        .btn-danger {
          background: linear-gradient(135deg, #c62828, #e53935);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
        }

        .page-header {
          background: linear-gradient(135deg, #d32f2f 0%, #1565c0 100%);
          padding: 16px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 2px 14px rgba(0,0,0,.2);
        }
        .back-btn {
          background: rgba(255,255,255,.16);
          border: 1px solid rgba(255,255,255,.28);
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          color: white;
        }
        .back-btn:hover { background: rgba(255,255,255,.26); }
        .page-header h1 { margin: 0; font-size: 20px; color: white; }

        .content-wrap {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,.06);
          overflow: hidden;
        }

        .card-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 22px 14px;
        }
        .title-left { display: flex; align-items: center; gap: 12px; }
        .title-icon {
          width: 32px; height: 32px;
          background: #c0dd97;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #173404;
          font-size: 18px;
          font-weight: 700;
        }
        .title-text {
          font-size: 18px;
          font-weight: 700;
          color: #1565c0;
          letter-spacing: 0.5px;
        }
        .refresh-btn {
          background: white;
          border: 1px solid #d1d5db;
          padding: 7px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          font-size: 12px;
          color: #374151;
          letter-spacing: 0.5px;
        }
        .refresh-btn:hover { background: #f9fafb; }

        .tabs {
          display: flex;
          gap: 24px;
          border-bottom: 1px solid #e5e7eb;
          padding: 0 22px;
        }
        .tab {
          background: transparent;
          border: none;
          padding: 12px 2px;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          cursor: pointer;
          letter-spacing: 0.4px;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
        }
        .tab:hover { color: #374151; }
        .tab.active {
          color: #1565c0;
          border-bottom-color: #1565c0;
        }

        .filters {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          padding: 14px 22px;
          background: #fafafa;
          border-bottom: 1px solid #e5e7eb;
        }
        .filters select,
        .filters input {
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 12px;
          background: white;
          font-family: inherit;
        }
        .filters input[type="text"] { flex: 1; min-width: 160px; }
        .reset-btn {
          background: white;
          border: 1px solid #d1d5db;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          color: #6b7280;
        }

        .table-wrap { overflow-x: auto; }
        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .data-table thead tr {
          background: linear-gradient(to right, #534ab7, #3c3489);
          color: white;
        }
        .data-table th {
          padding: 11px 14px;
          text-align: left;
          font-weight: 600;
          font-size: 10px;
          letter-spacing: 0.5px;
        }
        .data-table tbody tr {
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          transition: background 0.15s;
        }
        .data-table tbody tr:hover { background: #fafafa; }
        .data-table tbody tr.row-pending { background: #fef9eb; }
        .data-table tbody tr.row-pending:hover { background: #fdf4d8; }
        .data-table tbody tr.row-expanded { background: #f3f4f6; }
        .data-table td {
          padding: 11px 14px;
          vertical-align: middle;
        }
        .cell-id { font-weight: 600; color: #111827; }
        .cell-muted { color: #6b7280; }
        .cell-strong { font-weight: 600; color: #111827; }
        .cell-reason {
          color: #6b7280;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .cell-time { color: #9ca3af; font-size: 11px; white-space: nowrap; }
        .cell-actions { text-align: right; white-space: nowrap; }
        .cell-audit { color: #9ca3af; font-size: 11px; font-style: italic; }

        .pill {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.3px;
        }

        .status-pill {
          display: inline-block;
          padding: 3px 12px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.3px;
        }
        .status-pending  { background: #fac775; color: #412402; }
        .status-approved { background: #c0dd97; color: #173404; }
        .status-rejected { background: #f7c1c1; color: #501313; }

        .btn-approve, .btn-reject {
          padding: 5px 11px;
          border: none;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 600;
          cursor: pointer;
          margin-left: 4px;
          letter-spacing: 0.4px;
        }
        .btn-approve { background: #1d9e75; color: white; }
        .btn-approve:hover { background: #178a64; }
        .btn-reject  { background: #a32d2d; color: white; }
        .btn-reject:hover  { background: #8e2424; }

        .row-detail td { background: #fafbfc; padding: 18px 22px; }
        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 24px;
        }
        .detail-grid h4 {
          font-size: 11px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0 0 8px;
          font-weight: 700;
        }
        .detail-grid ul {
          list-style: none;
          padding: 0;
          margin: 0;
          font-size: 12px;
        }
        .detail-grid li { padding: 3px 0; color: #374151; }
        .kv-key { color: #9ca3af; }
        .detail-grid .muted { color: #9ca3af; font-size: 12px; font-style: italic; margin: 0; }

        .empty-state {
          text-align: center;
          padding: 60px 24px;
          color: #6b7a8f;
        }
        .empty-icon { font-size: 40px; margin-bottom: 12px; opacity: 0.5; }
        .empty-title { font-size: 15px; font-weight: 600; color: #374151; margin-bottom: 4px; text-transform: capitalize; }
        .empty-sub { font-size: 12px; color: #9ca3af; }

        @media (max-width: 900px) {
          .detail-grid { grid-template-columns: 1fr; }
          .filters input[type="text"] { min-width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default EditRequests;
